import { useEffect, useMemo, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";

import { Button, Icon, Input, Modal, Notification, Select } from "@stellar/design-system";

import { DirectPaymentConfirmation } from "@/components/DirectPaymentConfirmation/DirectPaymentConfirmation";
import { DistributionAccountLabel } from "@/components/DistributionAccountLabel";
import { ErrorWithExtras } from "@/components/ErrorWithExtras";
import { SelectedReceiverInfo } from "@/components/SelectedReceiverInfo/SelectedReceiverInfo";

import { directPayment } from "@/constants/directPayment";

import { useAllAssets } from "@/apiQueries/useAllAssets";
import { useDistributionWalletBalance } from "@/apiQueries/useDistributionWalletBalance";
import { DistributionWallet } from "@/apiQueries/useDistributionWallets";
import { useReceiversReceiverId } from "@/apiQueries/useReceiversReceiverId";
import { useSearchReceivers } from "@/apiQueries/useSearchReceivers";
import { useWallets } from "@/apiQueries/useWallets";

import { distributionAccountDisplayName } from "@/helpers/distributionAccountDisplayName";
import { getEnhancedWalletErrorMessage } from "@/helpers/walletErrorMessages";
import { isValidWalletAddress } from "@/helpers/walletValidate";

import { useDebounce } from "@/hooks/useDebounce";
import { usePrevious } from "@/hooks/usePrevious";
import { useRedux } from "@/hooks/useRedux";

import { ApiAssetWithTrustline, ApiReceiver, CreateDirectPaymentRequest } from "@/types";

import "./styles.scss";

interface DirectPaymentCreateModalProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (paymentData: CreateDirectPaymentRequest, sourceWalletId?: string) => void;
  onResetQuery: () => void;
  isLoading: boolean;
  errorMessage?: string;
  // The distribution (sending) accounts this payment can leave from. The choice is made and kept
  // here, in the modal, so opening the flow never re-scopes the rest of the app.
  sourceWallets?: DistributionWallet[];
  initialSourceWalletId?: string;
}

// A compact "Sending from [dot] Account" banner so the source account is visible at the point
// of committing a direct payment (single-account tenants don't need it).
const SendingFromBanner = ({ wallet }: { wallet?: DistributionWallet }) => {
  if (!wallet) {
    return null;
  }
  return (
    <div className="DirectPaymentCreateModal__sendingFrom">
      <span className="DirectPaymentCreateModal__sendingFrom__label">Sending from</span>
      <span className="DirectPaymentCreateModal__sendingFrom__value">
        <DistributionAccountLabel wallet={wallet} />
      </span>
    </div>
  );
};

const INITIAL_FORM_DATA = {
  assetId: "",
  amount: "",
  receiverSearch: "",
  selectedReceiver: null as ApiReceiver | null,
  walletId: "",
  externalPaymentId: "",
};

export const DirectPaymentCreateModal: React.FC<DirectPaymentCreateModalProps> = ({
  visible,
  onClose,
  onSubmit,
  onResetQuery,
  isLoading,
  errorMessage,
  sourceWallets,
  initialSourceWalletId,
}) => {
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [paymentDataToConfirm, setPaymentDataToConfirm] =
    useState<CreateDirectPaymentRequest | null>(null);
  const [selectedReceiverId, setSelectedReceiverId] = useState<string | undefined>(undefined);
  // Kept out of formData: it isn't a validated form field, and the asset/receiver cascades in
  // handleInputChange clear their sibling fields on every change.
  const [sourceWalletId, setSourceWalletId] = useState(initialSourceWalletId ?? "");
  const queryClient = useQueryClient();
  const debouncedReceiverSearch = useDebounce(
    formData.receiverSearch,
    directPayment.SEARCH_DEBOUNCE_MS,
  );
  const previousVisible = usePrevious(visible);
  // Single-account tenants have nothing to choose between, so they get neither selector nor banner.
  const canChooseSource = (sourceWallets?.length ?? 0) >= 2;
  // Assets the payment can be made in are the SOURCE's trustlines, so the list follows the
  // modal's own source (X-Wallet-Id), not the app-wide selection. The exact balance still comes
  // from the per-wallet endpoint further down.
  const { data: allAssets } = useAllAssets({
    enabled: true,
    walletId: canChooseSource ? sourceWalletId : undefined,
  });

  // Fetch full receiver details including verifications when a receiver is selected
  // Using dataFormat 'raw' to get raw API response without formatting
  const { data: receiverDetailsRaw } = useReceiversReceiverId<ApiReceiver>({
    receiverId: selectedReceiverId,
    dataFormat: "raw",
  });
  const selectedAsset = useMemo(
    () =>
      allAssets?.find((asset) => asset.id === formData.assetId) as
        | ApiAssetWithTrustline
        | undefined,
    [allAssets, formData.assetId],
  );
  // Fetch wallets - for verified receivers, explicitly get only SEP-24 wallets
  const isReceiverVerified = Boolean(formData.selectedReceiver?.verifications?.length);
  const { data: supportedWallets = [], isLoading: walletsLoading } = useWallets({
    supportedAssets: selectedAsset ? [selectedAsset.code] : [],
    // For verified receivers, only get SEP-24 wallets (user_managed = false)
    // For non-verified, get all wallets (undefined = no filter)
    userManaged: isReceiverVerified ? false : undefined,
  });
  const sourceWallet = sourceWallets?.find((w) => w.id === sourceWalletId);
  // Scope the receiver search to the account this payment will actually leave from — the
  // modal's own source, NOT the app-wide selection. Under "All accounts" (or after picking a
  // different source here) the ambient scope would offer receivers outside the source's reach
  // and then submit the payment against the source anyway.
  const { data: searchResults, isLoading: searchLoading } = useSearchReceivers(
    debouncedReceiverSearch,
    // Only wait on a source where one is actually chosen; single-account tenants (and
    // deployments without the distribution-wallets routes) never resolve one.
    !canChooseSource || Boolean(sourceWalletId),
    sourceWalletId || null,
  );
  const { userAccount } = useRedux("userAccount");
  const { data: sourceWalletBalance, isError: isSourceBalanceError } = useDistributionWalletBalance(
    Boolean(userAccount.isAuthenticated) && visible && canChooseSource && Boolean(sourceWalletId),
    sourceWalletId || null,
  );
  // The balance of the selected asset on the selected source, or undefined when the source's
  // balance doesn't apply (single-account tenant) or isn't known yet. Balances come back keyed
  // "XLM" for the native asset and "CODE:ISSUER" for everything else.
  // (The query key is shared with the account switcher, so gate on the source explicitly:
  // cached data can be present even while this query is disabled.)
  const sourceAssetBalance = useMemo(() => {
    if (!canChooseSource || !sourceWalletId || !selectedAsset || !sourceWalletBalance) {
      return undefined;
    }
    const key = selectedAsset.issuer?.trim()
      ? `${selectedAsset.code}:${selectedAsset.issuer}`
      : "XLM";
    return sourceWalletBalance.balances?.[key] ?? "0";
  }, [canChooseSource, sourceWalletId, selectedAsset, sourceWalletBalance]);
  const filteredWallets = useMemo(() => {
    if (!supportedWallets.length || !formData.assetId || !formData.selectedReceiver) {
      return [];
    }

    const receiverWalletIds = formData.selectedReceiver.wallets.map((w) => w.wallet.id);
    const isReceiverVerified = Boolean(formData.selectedReceiver.verifications?.length);

    // For verified receivers with SEP-24 wallets, show all enabled SEP-24 wallets
    // For regular receivers, only show pre-registered wallets
    if (isReceiverVerified) {
      return supportedWallets.filter((wallet) => wallet.enabled && !wallet.user_managed);
    }

    return supportedWallets.filter(
      (wallet) => wallet.enabled && receiverWalletIds.includes(wallet.id),
    );
  }, [supportedWallets, formData.assetId, formData.selectedReceiver]);
  const isWalletFieldDisabled = !formData.selectedReceiver || walletsLoading;
  const isWalletFieldLoading = walletsLoading && Boolean(formData.selectedReceiver);
  const isReceiverSearchLoading =
    searchLoading && debouncedReceiverSearch.trim().length >= directPayment.SEARCH_MIN_CHARS;
  const getWalletFieldNote = (): string => {
    if (!formData.selectedReceiver) return "Select a receiver first";
    if (isWalletFieldLoading) return "Loading compatible wallets…";
    if (filteredWallets.length === 0) return "No compatible wallets available for this receiver";

    const isReceiverVerified = Boolean(formData.selectedReceiver.verifications?.length);
    if (isReceiverVerified) {
      const registeredCount = filteredWallets.filter((w) => {
        const rw = formData.selectedReceiver?.wallets?.find((wr) => wr.wallet.id === w.id);
        return rw && rw.stellar_address;
      })?.length;

      if (registeredCount === filteredWallets.length) {
        return `${filteredWallets.length} wallet(s) available`;
      } else {
        return `${filteredWallets.length} wallet(s) available - Not Registered wallets will receive an invitation after payment creation`;
      }
    }
    return `${filteredWallets.length} compatible wallet(s) available`;
  };
  // Where a source account is chosen, the balance shown is that account's. Falling back to the
  // asset list's balance would quietly show the tenant default account's number instead, so
  // while the source's balance is in flight (or failed) we say so rather than show a figure
  // that belongs to a different account.
  const getAssetFieldNote = (): string | undefined => {
    if (!selectedAsset) {
      return undefined;
    }
    if (canChooseSource && sourceWalletId) {
      if (isSourceBalanceError) {
        return "Could not load this account's balance";
      }
      if (sourceAssetBalance === undefined) {
        return "Loading available balance…";
      }
      return `Available balance: ${sourceAssetBalance} ${selectedAsset.code}`;
    }
    return `Available balance: ${selectedAsset.balance} ${selectedAsset.code}`;
  };

  const isAmountValid =
    formData.amount.trim() !== "" && !isNaN(Number(formData.amount)) && Number(formData.amount) > 0;
  const isReceiverValid =
    formData.receiverSearch.trim() !== "" &&
    (isValidWalletAddress(formData.receiverSearch) || Boolean(formData.selectedReceiver));
  const isWalletValid = isValidWalletAddress(formData.receiverSearch)
    ? true
    : Boolean(formData.walletId);
  const isSubmitEnabled = Boolean(
    formData.assetId && isAmountValid && isReceiverValid && isWalletValid,
  );

  useEffect(() => {
    if (previousVisible && !visible) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setFormData(INITIAL_FORM_DATA);
      setFormErrors({});
      setShowConfirmation(false);
      setPaymentDataToConfirm(null);
      setSelectedReceiverId(undefined);
    }
    // Re-seed on every open so a source account picked for an earlier payment can't silently
    // fund the next one.
    if (!previousVisible && visible) {
      setSourceWalletId(initialSourceWalletId ?? "");
    }
  }, [visible, previousVisible, initialSourceWalletId]);

  useEffect(() => {
    if (receiverDetailsRaw && formData.selectedReceiver?.id === receiverDetailsRaw.id) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setFormData((prev) => ({
        ...prev,
        selectedReceiver: {
          ...prev.selectedReceiver!,
          verifications: receiverDetailsRaw.verifications || [],
        },
      }));
    }
  }, [receiverDetailsRaw, formData.selectedReceiver?.id]);

  const handleClose = () => {
    if (showConfirmation) {
      setShowConfirmation(false);
      setPaymentDataToConfirm(null);
    } else {
      onClose();
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    if (errorMessage) {
      onResetQuery();
    }

    const { id, value } = event.target;

    if (formErrors[id]) {
      setFormErrors((prev) => ({ ...prev, [id]: "" }));
    }

    if (id === "assetId") {
      setFormData((prev) => ({
        ...prev,
        assetId: value,
        receiverSearch: "",
        selectedReceiver: null,
        walletId: "",
      }));
      setFormErrors((prev) => ({
        ...prev,
        receiverSearch: "",
        walletId: "",
      }));
      // Clear any cached receiver search results when changing asset or clearing the field
      queryClient.removeQueries({ queryKey: ["receivers", "search"] });
      return;
    }

    if (id === "receiverSearch") {
      const shouldClearReceiver = formData.selectedReceiver && value !== formData.receiverSearch;

      setFormData((prev) => ({
        ...prev,
        receiverSearch: value,
        selectedReceiver: shouldClearReceiver ? null : prev.selectedReceiver,
        walletId: "",
      }));

      if (shouldClearReceiver) {
        setSelectedReceiverId(undefined);
      }

      setFormErrors((prev) => ({ ...prev, walletId: "" }));
      if (value.trim().length < directPayment.SEARCH_MIN_CHARS) {
        // Proactively remove cached results so we don't show stale data
        queryClient.removeQueries({ queryKey: ["receivers", "search"] });
      }
      return;
    }

    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  // Everything below the source account is scoped to it: receivers come from that account's
  // scope, and the amount is shown/validated against that account's balance. Carrying any of it
  // across a source change would let a receiver picked under account A be submitted from
  // account B without ever being revalidated, so the dependent fields start over.
  const handleSourceWalletChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const nextSourceWalletId = event.target.value;
    if (nextSourceWalletId === sourceWalletId) {
      return;
    }

    if (errorMessage) {
      onResetQuery();
    }

    setSourceWalletId(nextSourceWalletId);
    // externalPaymentId is the organization's own identifier for the payment — it isn't scoped
    // to an account, so it survives.
    setFormData((prev) => ({ ...INITIAL_FORM_DATA, externalPaymentId: prev.externalPaymentId }));
    setFormErrors({});
    setSelectedReceiverId(undefined);
    queryClient.removeQueries({ queryKey: ["receivers", "search"] });
  };

  const handleReceiverSelect = (receiver: ApiReceiver) => {
    setSelectedReceiverId(receiver.id);

    setFormData((prev) => ({
      ...prev,
      receiverSearch: getReceiverDisplayInfo(receiver, formData.receiverSearch),
      selectedReceiver: receiver,
      walletId: "",
    }));
    setFormErrors((prev) => ({ ...prev, receiverSearch: "" }));
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!validateForm()) return;

    const isAddr = isValidWalletAddress(formData.receiverSearch);
    const selectedWallet = supportedWallets.find((w) => w.id === formData.walletId);

    const paymentData: CreateDirectPaymentRequest = {
      amount: formData.amount.trim(),
      asset: {
        id: selectedAsset?.id,
        code: selectedAsset?.code,
        issuer: selectedAsset?.issuer,
      },
      receiver: isAddr
        ? { wallet_address: formData.receiverSearch.trim() }
        : { id: formData.selectedReceiver!.id },
      ...(isAddr
        ? { wallet: { address: formData.receiverSearch.trim() } }
        : selectedWallet && { wallet: { id: selectedWallet.id } }),
      ...(formData.externalPaymentId.trim() && {
        external_payment_id: formData.externalPaymentId.trim(),
      }),
    };

    setPaymentDataToConfirm(paymentData);
    setShowConfirmation(true);
  };

  const handleConfirmPayment = () => {
    if (paymentDataToConfirm) {
      onSubmit(paymentDataToConfirm, sourceWalletId || undefined);
    }
  };

  const getReceiverDisplayInfo = (receiver: ApiReceiver, searchQuery: string): string => {
    const query = searchQuery.toLowerCase();
    if (receiver.email?.toLowerCase().includes(query)) return receiver.email;
    if (receiver.phone_number?.includes(query)) return receiver.phone_number;
    if (receiver.external_id?.toLowerCase().includes(query)) return receiver.external_id;
    return receiver.phone_number || receiver.email || receiver.external_id || "";
  };

  const showReceiverField = Boolean(formData.assetId);
  const isReceiverWalletAddress = isValidWalletAddress(formData.receiverSearch);
  const showWalletField = formData.assetId && !isReceiverWalletAddress;

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (!formData.assetId) errors.assetId = "Asset selection is required";
    if (!formData.amount.trim()) {
      errors.amount = "Amount is required";
    } else if (isNaN(Number(formData.amount)) || Number(formData.amount) <= 0) {
      errors.amount = "Amount must be a valid positive number";
    } else if (
      // Only when we actually know the chosen source's balance — sourceAssetBalance stays
      // undefined for single-account tenants and while/if the balance can't be read, so this
      // never blocks Review on a number we aren't sure of.
      sourceAssetBalance !== undefined &&
      Number(formData.amount) > Number(sourceAssetBalance)
    ) {
      errors.amount = `Amount exceeds this account's available balance (${sourceAssetBalance} ${selectedAsset?.code})`;
    }
    if (!formData.receiverSearch.trim()) {
      errors.receiverSearch = "Receiver is required";
    } else if (!isValidWalletAddress(formData.receiverSearch) && !formData.selectedReceiver) {
      errors.receiverSearch =
        "Please select a receiver from search results or enter a valid wallet address";
    }
    if (!isValidWalletAddress(formData.receiverSearch) && !formData.walletId) {
      errors.walletId = "Wallet selection is required";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const renderSearchResults = () => {
    if (!formData.receiverSearch || formData.selectedReceiver) return null;

    if (isReceiverSearchLoading) {
      return (
        <div className="DirectPaymentCreateModal__searchResults">
          <div className="DirectPaymentCreateModal__searchResult">Searching…</div>
        </div>
      );
    }

    if (
      !isReceiverWalletAddress &&
      formData.receiverSearch &&
      debouncedReceiverSearch.trim().length >= directPayment.SEARCH_MIN_CHARS &&
      searchResults?.data?.length === 0
    ) {
      return (
        <div className="DirectPaymentCreateModal__noResults">
          No receivers found. Make sure the receiver exists or enter a wallet address.
        </div>
      );
    }

    if (
      debouncedReceiverSearch.trim().length >= directPayment.SEARCH_MIN_CHARS &&
      searchResults?.data &&
      searchResults.data.length > 0
    ) {
      return (
        <div className="DirectPaymentCreateModal__searchResults">
          <div className="DirectPaymentCreateModal__searchResults__content">
            {searchResults.data.slice(0, directPayment.MAX_SEARCH_RESULTS).map((receiver) => {
              const isSelected = formData.selectedReceiver?.id === receiver.id;
              return (
                <div
                  key={receiver.id}
                  className="DirectPaymentCreateModal__searchResult"
                  onClick={() => handleReceiverSelect(receiver)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleReceiverSelect(receiver);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={`Select receiver ${getReceiverDisplayInfo(receiver, formData.receiverSearch)}`}
                >
                  <div>
                    <div className="DirectPaymentCreateModal__searchResultMain">
                      {getReceiverDisplayInfo(receiver, formData.receiverSearch)}
                    </div>
                    <div className="DirectPaymentCreateModal__searchResultSub">
                      ID: {receiver.id.slice(0, directPayment.RECEIVER_ID_PREVIEW_LENGTH)}… •{" "}
                      {receiver.wallets.length} wallet(s)
                    </div>
                  </div>
                  {isSelected && <Icon.CheckCircle />}
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    return null;
  };

  const renderWalletOptions = () => {
    return filteredWallets.map((wallet) => {
      const recvWallet = formData.selectedReceiver?.wallets?.find((w) => w.wallet.id === wallet.id);
      const addr = recvWallet?.stellar_address || "";
      const shortAddr = addr && `${addr.slice(0, 4)}…${addr.slice(-4)}`;
      const isRegistered = Boolean(recvWallet && addr);

      // Show registration status and address based on wallet state
      let label = wallet.name;
      if (isRegistered) {
        label = `${wallet.name} - Registered (${shortAddr})`;
      } else if (recvWallet) {
        // Wallet exists but no address yet - in registration process
        label = `${wallet.name} - Not Registered (pending)`;
      } else {
        // Will be auto-registered for verified receivers
        const isVerified = Boolean(formData.selectedReceiver?.verifications?.length);
        if (isVerified && !wallet.user_managed) {
          label = `${wallet.name} - Not Registered`;
        } else {
          label = `${wallet.name}`;
        }
      }

      return (
        <option key={wallet.id} value={wallet.id}>
          {label}
        </option>
      );
    });
  };

  return (
    <Modal visible={visible} onClose={handleClose}>
      <Modal.Heading>
        {showConfirmation ? "Confirm direct payment" : "Create a direct payment"}
      </Modal.Heading>
      {showConfirmation ? (
        <>
          <Modal.Body>
            <SendingFromBanner wallet={canChooseSource ? sourceWallet : undefined} />
            {errorMessage && (
              <Notification variant="error" title="Error" isFilled={true}>
                <ErrorWithExtras
                  appError={{ message: getEnhancedWalletErrorMessage(errorMessage) }}
                />
              </Notification>
            )}
            {paymentDataToConfirm && (
              <DirectPaymentConfirmation
                paymentData={paymentDataToConfirm}
                selectedReceiver={formData.selectedReceiver}
                receiverSearch={formData.receiverSearch}
                selectedWalletInfo={supportedWallets.find((w) => w.id === formData.walletId)}
              />
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              size="md"
              variant="tertiary"
              onClick={() => setShowConfirmation(false)}
              disabled={isLoading}
            >
              Back
            </Button>
            <Button
              size="md"
              variant="primary"
              onClick={handleConfirmPayment}
              disabled={isLoading}
              isLoading={isLoading}
            >
              Confirm Payment
            </Button>
          </Modal.Footer>
        </>
      ) : (
        <form onSubmit={handleSubmit} onReset={handleClose}>
          <Modal.Body>
            <div className="DirectPaymentCreateModal__description">
              Send a single payment directly to a receiver via email, phone number, or wallet
              address.
            </div>
            {errorMessage && (
              <Notification variant="error" title="Error" isFilled={true}>
                <ErrorWithExtras
                  appError={{ message: getEnhancedWalletErrorMessage(errorMessage) }}
                />
              </Notification>
            )}

            <div className="DirectPaymentCreateModal__form">
              {/* Source Account — the operator confirms which account funds this payment instead
                  of being moved onto one behind their back. */}
              {canChooseSource && (
                <Select
                  fieldSize="sm"
                  id="sourceWalletId"
                  label="Sending from"
                  value={sourceWalletId}
                  onChange={handleSourceWalletChange}
                  required
                >
                  <option value="">Select…</option>
                  {sourceWallets?.map((wallet) => (
                    <option key={wallet.id} value={wallet.id}>
                      {distributionAccountDisplayName(wallet)}
                    </option>
                  ))}
                </Select>
              )}

              {/* Asset Selection */}
              <Select
                fieldSize="sm"
                id="assetId"
                label="Asset"
                value={formData.assetId}
                onChange={handleInputChange}
                error={formErrors.assetId}
                note={getAssetFieldNote()}
                required
              >
                <option value="">Select…</option>
                {allAssets?.map((asset) => (
                  <option key={asset.id} value={asset.id}>
                    {asset.code}{" "}
                    {asset.code !== "XLM" &&
                      `(${asset.issuer.slice(0, directPayment.ASSET_ISSUER_PREVIEW_LENGTH / 2)}…${asset.issuer.slice(-directPayment.ASSET_ISSUER_PREVIEW_LENGTH / 2)})`}
                  </option>
                ))}
              </Select>

              {/* Amount */}
              <Input
                fieldSize="sm"
                id="amount"
                name="amount"
                type="text"
                label="Amount"
                placeholder="Enter amount"
                value={formData.amount}
                onChange={handleInputChange}
                error={formErrors.amount}
                rightElement={
                  selectedAsset?.code ? (
                    <span className="DirectPaymentCreateModal__currency">{selectedAsset.code}</span>
                  ) : undefined
                }
                disabled={!formData.assetId}
                required
              />

              {/* Receiver Search */}
              <div
                className="DirectPaymentCreateModal__fieldWrapper"
                data-visible={showReceiverField}
              >
                <div className="DirectPaymentCreateModal__fieldWrapper__content">
                  <Input
                    fieldSize="sm"
                    id="receiverSearch"
                    label="Receiver"
                    placeholder="Search by email, phone, or enter wallet address (GXXX…)"
                    infoText="Search results appear after entering at least 3 characters"
                    tooltipPlacement="top-start"
                    value={formData.receiverSearch}
                    onChange={handleInputChange}
                    error={formErrors.receiverSearch}
                    note={isReceiverWalletAddress && "Wallet address detected"}
                    required
                  />

                  {formData.selectedReceiver && !isReceiverWalletAddress && (
                    <SelectedReceiverInfo receiver={formData.selectedReceiver} />
                  )}

                  {renderSearchResults()}
                </div>
              </div>

              {/* Wallet Selection */}
              <div
                className="DirectPaymentCreateModal__fieldWrapper"
                data-visible={showReceiverField && showWalletField}
              >
                <div className="DirectPaymentCreateModal__fieldWrapper__content">
                  <Select
                    fieldSize="sm"
                    id="walletId"
                    label="Wallet"
                    value={formData.walletId}
                    onChange={handleInputChange}
                    error={formErrors.walletId}
                    disabled={isWalletFieldDisabled}
                    note={getWalletFieldNote()}
                    required
                  >
                    <option value="">Select…</option>
                    {renderWalletOptions()}
                  </Select>
                </div>
              </div>

              {/* External Payment ID */}
              <div
                className="DirectPaymentCreateModal__fieldWrapper"
                data-visible={showReceiverField}
              >
                <div className="DirectPaymentCreateModal__fieldWrapper__content">
                  <Input
                    fieldSize="sm"
                    id="externalPaymentId"
                    name="externalPaymentId"
                    type="text"
                    infoText="An identifier for the payment defined by your organization"
                    tooltipPlacement="top-start"
                    label="Enter external payment ID (optional)"
                    placeholder="Enter external payment ID"
                    value={formData.externalPaymentId}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button size="md" variant="tertiary" type="reset" disabled={isLoading}>
              Cancel
            </Button>
            <Button
              size="md"
              variant="primary"
              type="submit"
              disabled={!isSubmitEnabled}
              isLoading={isLoading}
            >
              Review
            </Button>
          </Modal.Footer>
        </form>
      )}
    </Modal>
  );
};
